import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
export function usePayments(filters = {}) {
    return useQuery({
        queryKey: ['payments', filters],
        queryFn: () => {
            const params = new URLSearchParams();
            if (filters.student_id)
                params.set('student_id', filters.student_id);
            if (filters.month !== undefined)
                params.set('month', String(filters.month));
            if (filters.year !== undefined)
                params.set('year', String(filters.year));
            if (filters.status)
                params.set('status', filters.status);
            if (filters.opposition_id)
                params.set('opposition_id', filters.opposition_id);
            const qs = params.toString();
            return apiClient(`/payments${qs ? `?${qs}` : ''}`);
        },
        staleTime: 1000 * 60 * 2,
    });
}
export function useRegisterPayment() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (dto) => apiClient('/payments', { method: 'POST', body: JSON.stringify(dto) }),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['payments'] }),
    });
}
export function useUpdatePayment(id) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (dto) => apiClient(`/payments/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(dto),
        }),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['payments'] }),
    });
}
/** Marks a pending/overdue payment as paid with the chosen collection date
 *  (YYYY-MM-DD, today or earlier — the backend rejects future dates). */
export function useMarkPaymentPaid() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, paidAt }) => apiClient(`/payments/${id}`, {
            method: 'PATCH',
            body: JSON.stringify({ status: 'paid', paid_at: paidAt }),
        }),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['payments'] }),
    });
}
/** Switches a pending monthly payment between the full fee and the reduced (65€) one. */
export function useSetPaymentFee() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, reduced }) => apiClient(`/payments/${id}/fee`, {
            method: 'PATCH',
            body: JSON.stringify({ reduced }),
        }),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['payments'] }),
    });
}
/** Marks a pending/overdue payment as exempt (not collectible: scholarships,
 *  students enrolled ahead of their real start month, etc.). */
export function useExemptPayment() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => apiClient(`/payments/${id}`, {
            method: 'PATCH',
            body: JSON.stringify({ status: 'exempt' }),
        }),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['payments'] }),
    });
}
/** Reverts a paid or exempt payment back to pending (the backend clears
 *  paid_at when it was paid). For correcting a mistaken charge or exemption. */
export function useRevertPaymentToPending() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => apiClient(`/payments/${id}`, {
            method: 'PATCH',
            body: JSON.stringify({ status: 'pending' }),
        }),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['payments'] }),
    });
}
//# sourceMappingURL=usePayments.js.map